import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { TestControl, TestCategory } from '../../models/test-control.interface';
import { TestControlComponent } from '../test-control/test-control.component';

/**
 * Component for displaying a grid of test controls organized by category
 */
@Component({
  selector: 'app-test-grid',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatBadgeModule,
    TestControlComponent
  ],
  template: `
    <mat-card class="test-category" [class]="'category-' + (category.color || 'primary')">
      <mat-card-header>
        <div mat-card-avatar class="category-avatar">
          <mat-icon>{{category.icon || 'category'}}</mat-icon>
        </div>
        <mat-card-title>
          <span class="title-text">{{category.title}}</span>
          <span class="test-count-badge" [matBadge]="tests.length" matBadgeColor="accent" matBadgeOverlap="false">
          </span>
        </mat-card-title>
        <mat-card-subtitle>{{category.description}}</mat-card-subtitle>
      </mat-card-header>
      
      <mat-card-content>
        <div *ngIf="tests.length === 0" class="empty-state">
          <mat-icon>hourglass_empty</mat-icon>
          <p>No tests registered for this category</p>
        </div>
        
        <div *ngIf="tests.length > 0" class="test-grid">
          <app-test-control 
            *ngFor="let test of tests; trackBy: trackByTestId"
            [test]="test">
          </app-test-control>
        </div>
      </mat-card-content>
    </mat-card>
  `,
  styles: [`
    .test-category {
      margin-bottom: 24px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    .category-avatar {
      background: #2196f3;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
    }

    .category-primary .category-avatar {
      background: #2196f3;
    }

    .category-accent .category-avatar {
      background: #ff4081;
    }

    .category-warn .category-avatar {
      background: #f44336;
    }

    .category-success .category-avatar {
      background: #4caf50;
    }

    .category-info .category-avatar {
      background: #00bcd4;
    }

    mat-card-title {
      display: flex;
      align-items: center;
      gap: 12px;
      position: relative;
    }

    .title-text {
      flex: 1;
    }

    .test-count-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 24px;
      position: relative;
      margin-left: 8px;
    }
    
    /* Ensure badge text is visible */
    .test-count-badge ::ng-deep .mat-badge-content {
      font-size: 12px;
      font-weight: 500;
    }

    .test-count {
      font-size: 12px;
      font-weight: normal;
      color: #666;
    }

    .test-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
      margin-top: 16px;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: #999;
      text-align: center;
    }

    .empty-state mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      margin-bottom: 16px;
    }

    .empty-state p {
      margin: 0;
      font-size: 14px;
    }

    /* Responsive design */
    @media (max-width: 767px) {
      .test-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 12px;
      }
    }

    @media (max-width: 480px) {
      .test-grid {
        grid-template-columns: 1fr;
        gap: 8px;
      }
    }
  `]
})
export class TestGridComponent {
  @Input() category!: TestCategory;
  @Input() tests!: TestControl[];

  /**
   * Track by function for efficient rendering
   */
  trackByTestId(index: number, test: TestControl): string {
    return test.id;
  }
}